import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Search, Download, CheckCircle, Plus, Edit, Trash2, Link2 } from 'lucide-react';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReceiptsList = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const filterMode = searchParams.get('filter') || 'all'; // 'all' | 'standalone'
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReceipts(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (err) {
      logger.error('Failed to fetch receipts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (receipt) => {    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${receipt.id}/download-pdf`, {
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Receipt_${receipt.client_name.replace(/\s/g, '_')}_${receipt.receipt_number.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (err) {
      logger.error('Download error:', err);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const getPaymentMethodBadge = (method) => {
    const methodMap = {
      'bank_transfer': { label: 'Bank Transfer', color: 'bg-blue-100 text-blue-800 border-blue-200' },
      'wire': { label: 'Wire Transfer', color: 'bg-purple-100 text-purple-800 border-purple-200' },
      'cash': { label: 'Cash', color: 'bg-green-100 text-green-800 border-green-200' },
      'card': { label: 'Card', color: 'bg-orange-100 text-orange-800 border-orange-200' }
    };
    const info = methodMap[method] || { label: method, color: 'bg-gray-100 text-gray-800 border-gray-200' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${info.color}`}>
        {info.label}
      </span>
    );
  };

  const handleDelete = async (receipt) => {
    if (!window.confirm(`Delete receipt ${receipt.receipt_number}? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${receipt.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      if (response.ok) {
        setReceipts(receipts.filter(r => r.id !== receipt.id));
      } else {
        alert('Failed to delete receipt');
      }
    } catch (err) {
      logger.error('Delete receipt error:', err);
      alert('Failed to delete receipt');
    }
  };

  const filteredReceipts = receipts.filter(receipt => {
    if (filterMode === 'standalone' && (receipt.invoice_id || receipt.quote_id)) return false;
    const q = searchTerm.toLowerCase();
    return (receipt.client_name || '').toLowerCase().includes(q) ||
           (receipt.receipt_number || '').toLowerCase().includes(q) ||
           (receipt.invoice_number || '').toLowerCase().includes(q);
  });

  const standaloneCount = receipts.filter(r => !r.invoice_id && !r.quote_id).length;

  const setFilter = (mode) => {
    const next = new URLSearchParams(searchParams);
    if (mode === 'all') next.delete('filter'); else next.set('filter', mode);
    setSearchParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground/60">Loading receipts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between admin-header-row">
            <div>
              <h1 className="text-2xl font-bold text-primary">Payment Receipts</h1>
              <p className="text-sm text-foreground/60">Auto-generated payment confirmations</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate('/admin/receipts/new')}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                data-testid="create-receipt-btn"
              >
                <Plus className="w-4 h-4" />
                Create Receipt
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Dashboard
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" />
              <input
                type="text"
                placeholder="Search by client name, receipt or invoice number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="receipts-filter-tabs">
            <button
              onClick={() => setFilter('all')}
              data-testid="filter-tab-all"
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                filterMode === 'all'
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-foreground/70 border-border hover:bg-gray-50'
              }`}
            >
              All receipts ({receipts.length})
            </button>
            <button
              onClick={() => setFilter('standalone')}
              data-testid="filter-tab-standalone"
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors flex items-center gap-1.5 ${
                filterMode === 'standalone'
                  ? 'bg-red-600 text-white border-red-600'
                  : standaloneCount > 0
                    ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                    : 'bg-white text-foreground/60 border-border hover:bg-gray-50'
              }`}
            >
              <Link2 className="w-3 h-3" />
              Standalone (not linked to invoice/quote) — {standaloneCount}
            </button>
          </div>
        </div>
      </div>

      {/* Receipts Table */}
      <div className="container mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {filteredReceipts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-foreground/20 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No receipts found</h3>
              <p className="text-foreground/60 mb-4">Receipts are automatically generated when invoices are marked as paid</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Receipt #</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Invoice Ref</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Payment Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-foreground/60 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-foreground/60 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredReceipts.map((receipt) => (
                    <tr key={receipt.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm font-semibold text-green-600">{receipt.receipt_number}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {receipt.invoice_id ? (
                          <button
                            onClick={() => navigate(`/admin/invoices/${receipt.invoice_id}`)}
                            className="font-mono text-sm text-primary hover:underline"
                          >
                            {receipt.invoice_number}
                          </button>
                        ) : (
                          <span className="text-xs text-foreground/40 italic">
                            {receipt.invoice_number || 'Manual'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-foreground">{receipt.client_name}</div>
                          <div className="text-sm text-foreground/60">{receipt.client_email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground/70">
                        {formatDateShort(receipt.payment_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentMethodBadge(receipt.payment_method)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-semibold text-green-600">{receipt.currency}{receipt.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/receipts/${receipt.id}`)}
                            data-testid={`view-receipt-${receipt.id}`}
                            className="p-2 text-foreground/60 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            title="View"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/receipts/${receipt.id}/edit`)}
                            disabled={receipt.receipt_kind === 'combined'}
                            className="p-2 text-foreground/60 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={receipt.receipt_kind === 'combined' ? 'Issued combined receipt is read-only' : 'Edit'}
                            data-testid={`edit-receipt-${receipt.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadPDF(receipt)}
                            data-testid={`download-receipt-${receipt.id}`}
                            className="p-2 text-foreground/60 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(receipt)}
                            disabled={receipt.receipt_kind === 'combined'}
                            className="p-2 text-foreground/60 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={receipt.receipt_kind === 'combined' ? 'Preserved with its linked payment allocations' : 'Delete'}
                            data-testid={`delete-receipt-${receipt.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900">How Receipts Are Created</p>
            <p className="text-sm text-blue-700">
              Receipts are automatically created when invoices are marked as paid,
              or you can <strong>Create Receipt</strong> manually for payments received outside the invoice workflow.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
